// ResidentIQ triage. The backend classifier is authoritative; this module is
// the contract it fills and the client-side fallback used when
// POST /resident/triage isn't deployed yet.

export const CATEGORIES = [
  { id: "information", label: "Information request", blurb: "Answered from the governing documents" },
  { id: "architectural", label: "Architectural request", blurb: "Needs Architectural Review Committee approval" },
  { id: "maintenance", label: "Maintenance request", blurb: "Work order for a vendor or the manager" },
  { id: "document", label: "Document request", blurb: "A copy of a record the association holds" },
  { id: "other", label: "Something else", blurb: "Routed to the community inbox" },
];

export const MANAGER_ROLES = [
  { id: "association_manager", label: "Association Manager" },
  { id: "landscaping_manager", label: "Landscaping Manager" },
  { id: "water_plumbing", label: "Water and Plumbing" },
  { id: "trash_management", label: "Trash Management" },
  { id: "general_community", label: "General Community" },
];

export const categoryLabel = (id) => (CATEGORIES.find((c) => c.id === id) || CATEGORIES[4]).label;
export const managerLabel = (id) => (MANAGER_ROLES.find((m) => m.id === id) || MANAGER_ROLES[4]).label;

const has = (text, words) => words.some((w) => text.includes(w));

const AREA_RULES = [
  { area: "landscaping_manager", words: ["landscap", "tree", "lawn", "grass", "shrub", "irrigation", "sprinkler", "hedge", "weed", "garden", "mulch"] },
  { area: "water_plumbing", words: ["water", "leak", "plumb", "pipe", "sewer", "drain", "toilet", "faucet", "backflow", "hose bib", "flood"] },
  { area: "trash_management", words: ["trash", "garbage", "bin", "recycl", "dumpster", "refuse", "bulk pickup", "compost"] },
];

const CATEGORY_RULES = [
  { category: "architectural", words: ["paint", "remodel", "addition", "fence", "patio", "deck", "solar", "window", "door", "roof", "satellite", "awning", "arc ", "architectural", "exterior", "install", "ev charger", "heat pump"] },
  { category: "maintenance", words: ["broken", "repair", "fix", "leak", "clog", "not working", "damage", "pothole", "light out", "burned out", "overgrown", "graffiti", "pest", "rodent", "trim", "replace"] },
  { category: "document", words: ["copy of", "minutes", "budget", "reserve study", "financial", "statement", "insurance certificate", "resale", "escrow", "audit", "ledger", "records request"] },
  { category: "information", words: ["can i", "am i allowed", "what are", "what is", "when is", "how do i", "is it legal", "rules about", "policy on", "hours", "allowed to", "required to"] },
];

/**
 * Fallback classifier. Returns the same shape the backend should return.
 * @returns {{category:string, area:string, confidence:number, reasons:string[]}}
 */
export function classify(description) {
  const text = String(description || "").toLowerCase();
  const reasons = [];

  let category = "other";
  for (const rule of CATEGORY_RULES) {
    if (has(text, rule.words)) { category = rule.category; reasons.push("matched " + rule.category + " language"); break; }
  }

  let area = null;
  for (const rule of AREA_RULES) {
    if (has(text, rule.words)) { area = rule.area; reasons.push("subject matter → " + managerLabel(rule.area)); break; }
  }

  if (!area) {
    area = category === "architectural" || category === "document" || category === "maintenance"
      ? "association_manager"
      : "general_community";
  }

  // A maintenance word plus a vendor area is the strongest signal we have.
  const confidence = reasons.length >= 2 ? 0.86 : reasons.length === 1 ? 0.62 : 0.35;
  if (reasons.length === 0) reasons.push("no clear signal — routed to the community inbox");

  return { category, area, confidence, reasons };
}
